"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
  FC,
} from "react";
import {
  HoopsPasskeyKit,
  type CreateWalletResult,
  type FundResult,
  type TransactionResult,
  type LpPlan,
  type RedeemPlan,
} from "@/lib/hoops-passkey-kit";
import { HOOPS_CONTRACTS } from "@/lib/hoops-contracts";
import { useSession } from "@/utils/auth";

interface PasskeyContextType {
  kit: HoopsPasskeyKit | null;
  contractId: string | null;
  credentialId: string | null;
  deployerPublicKey: string | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  createWallet: (appName: string, userName: string) => Promise<CreateWalletResult | null>;
  deployWithExistingPasskey: (credentialId: string, p256PublicKeyHex: string) => Promise<CreateWalletResult | null>;
  connectWallet: () => Promise<void>;
  fundWallet: (xlmTokenContract: string) => Promise<FundResult | null>;
  transfer: (token: string, to: string, amount: bigint) => Promise<TransactionResult | null>;
  swap: (tokenIn: string, tokenOut: string, amount: bigint, bestHop: string, deadline: number) => Promise<TransactionResult | null>;
  deposit: (usdc: string, amount: bigint, lpPlans: LpPlan[], deadline: number) => Promise<TransactionResult | null>;
  redeemSoroswap: (pairAddress: string, lpAmount: bigint) => Promise<TransactionResult | null>;
  redeemAqua: (poolAddress: string, lpAmount: bigint) => Promise<TransactionResult | null>;
  claimAqua: (poolAddress: string) => Promise<TransactionResult | null>;
  batchRedeem: (plans: RedeemPlan[], deadline: number) => Promise<TransactionResult | null>;
  claim: (adapter: string, pool: string, rewardToken: string) => Promise<TransactionResult | null>;
  createSession: (scope: string[], maxAmount: bigint, ttlLedgers: number) => Promise<TransactionResult | null>;
  hasSession: boolean;
  queryBalance: (tokenId: string) => Promise<bigint>;
  getSwapQuotes: (tokenIn: string, tokenOut: string, amount: bigint) => Promise<import("@/lib/hoops-passkey-kit").SwapQuote[]>;
  queryAquaLpBalance: (shareTokenId: string) => Promise<bigint>;
  disconnect: () => void;
}

const PasskeyContext = createContext<PasskeyContextType | undefined>(undefined);

// V2 Account WASM hash with passkey + session key __check_auth (deployed to testnet)
const HOOPS_PASSKEY_WASM_HASH =
  process.env.NEXT_PUBLIC_HOOPS_ACCOUNT_WASM_HASH
  || "58e2cbac21100ec2b0c3e7ef0467fd4115d9fdf0cd1b520959702af65dd079e2";

/**
 * Derive the registrable domain from the current hostname.
 * e.g. "dashboard-ui-dev.hoops.fi" → "hoops.fi", "localhost" → "localhost"
 * This ensures the rpId matches the backend's WEBAUTHN_RP_ID (the parent domain)
 * without relying on build-time env vars that may not reach Docker.
 */
function deriveRpId(): string | undefined {
  if (typeof window === "undefined") return process.env.NEXT_PUBLIC_WEBAUTHN_RP_ID;
  const envVal = process.env.NEXT_PUBLIC_WEBAUTHN_RP_ID;
  if (envVal) return envVal;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return undefined; // let WebAuthn default
  const parts = host.split(".");
  if (parts.length <= 2) return host; // already a registrable domain
  return parts.slice(-2).join("."); // "dashboard-ui-dev.hoops.fi" → "hoops.fi"
}

const PASSKEY_CONFIG = {
  rpcUrl: process.env.NEXT_PUBLIC_STELLAR_RPC_URL || "https://soroban-testnet.stellar.org",
  networkPassphrase: process.env.NEXT_PUBLIC_NETWORK_PASSPHRASE || "Test SDF Network ; September 2015",
  wasmHash: HOOPS_PASSKEY_WASM_HASH,
  routerAddress: HOOPS_CONTRACTS.ROUTER,
};

export const PasskeyProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: session, status, update: updateSession } = useSession();
  const [contractId, setContractId] = useState<string | null>(null);
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kitRef = useRef<HoopsPasskeyKit | null>(null);
  const prevCredentialIdRef = useRef<string | null>(null);

  // Initialize kit once on mount (no localStorage access — that waits for session)
  useEffect(() => {
    if (kitRef.current) return;
    kitRef.current = new HoopsPasskeyKit({
      rpcUrl: PASSKEY_CONFIG.rpcUrl,
      networkPassphrase: PASSKEY_CONFIG.networkPassphrase,
      wasmHash: PASSKEY_CONFIG.wasmHash,
      routerAddress: PASSKEY_CONFIG.routerAddress,
      rpId: deriveRpId(),
      rpName: "Hoops Finance",
    });
  }, []);

  // ── Session-aware wallet lifecycle ──────────────────────────────────────
  // Waits for session to hydrate before touching localStorage.
  // Detects logout, user-switch, and session expiry — wipes stale state.
  useEffect(() => {
    if (status === "loading") return; // session not ready yet

    // CASE 1: Unauthenticated — clear in-memory state only.
    // localStorage is LEFT INTACT — scoped keys prevent cross-user access,
    // and the deployer keypair must survive logout/login cycles on the same device.
    if (status === "unauthenticated" || !session) {
      if (isConnected || contractId) {
        kitRef.current?.resetInMemory();
        setContractId(null);
        setCredentialId(null);
        setIsConnected(false);
      }
      prevCredentialIdRef.current = null;
      return;
    }

    const sessionCredentialId = session.user.credentialId ?? null;

    // CASE 2: Different user than before — wipe OLD user's scoped keys + in-memory state.
    // This is the only case where we delete localStorage (prevents cross-user leakage).
    if (
      prevCredentialIdRef.current &&
      sessionCredentialId &&
      prevCredentialIdRef.current !== sessionCredentialId
    ) {
      kitRef.current?.disconnect();
      HoopsPasskeyKit.clearAllWalletStorage();
      setContractId(null);
      setCredentialId(null);
      setIsConnected(false);
    }
    prevCredentialIdRef.current = sessionCredentialId;

    // CASE 3: Authenticated — reconnect with validation
    if (sessionCredentialId && !isConnected) {
      const sessionAddr = session.user.smart_account_address;
      void (async () => {
        const kit = kitRef.current;
        if (!kit) return;
        try {
          const result = await kit.connectWallet(sessionCredentialId);
          if (result?.contractId) {
            setContractId(result.contractId);
            setCredentialId(result.credentialId);
            setIsConnected(true);
            return;
          }
        } catch {
          // localStorage reconnect failed — fall through to session address
        }

        // Fallback: wallet exists in backend but no localStorage on this device.
        // Provision a new deployer (fee-payer) so signing works cross-device.
        // On testnet, Friendbot funds the deployer for free.
        if (sessionAddr && sessionCredentialId) {
          try {
            const p256Hex = session.user.p256PublicKeyHex;
            const transports = session.user.passkeyTransports as
              import("@simplewebauthn/browser").AuthenticatorTransportFuture[] | undefined;
            await kit.provisionForExistingWallet(
              sessionAddr,
              sessionCredentialId,
              p256Hex,
              transports,
            );
            setContractId(sessionAddr);
            setCredentialId(sessionCredentialId);
            setIsConnected(true);
          } catch (err) {
            console.warn("[PasskeyContext] Cross-device provisioning failed, falling back to read-only:", err);
            kit.setContractIdReadOnly(sessionAddr);
            setContractId(sessionAddr);
          }
        }
      })();
    }
  }, [status, session, isConnected, contractId]);

  // ── Cross-tab sync ─────────────────────────────────────────────────────
  // If another tab clears wallet storage (logout), sync this tab immediately.
  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      // A hoops_pk_ key was deleted — another tab logged out or switched user
      if (e.key?.startsWith("hoops_pk_") && e.newValue === null) {
        setContractId(null);
        setCredentialId(null);
        setIsConnected(false);
      }
    }
    window.addEventListener("storage", handleStorageChange);
    return () => { window.removeEventListener("storage", handleStorageChange); };
  }, []);

  const createWallet = useCallback(
    async (appName: string, userName: string): Promise<CreateWalletResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setIsLoading(true);
      setError(null);

      try {
        const result = await kit.createWallet(appName, userName);
        setContractId(result.contractId);
        setCredentialId(result.credentialId);
        setIsConnected(true);

        // Persist wallet address to NextAuth session (available cross-browser on next login)
        void updateSession({
          smart_account_address: result.contractId,
          credentialId: result.credentialId,
        }).catch(() => { /* session update is best-effort */ });

        // Persist wallet address to backend MongoDB (cross-device support)
        void fetch("/api/auth/passkey/wallet", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contractId: result.contractId,
            keyId: result.credentialId,
          }),
        }).catch(() => { /* backend persist is best-effort */ });

        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to create wallet";
        setError(msg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const deployWithExistingPasskey = useCallback(
    async (existingCredentialId: string, p256PublicKeyHex: string): Promise<CreateWalletResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setIsLoading(true);
      setError(null);

      try {
        // Pass transports from session so the kit can hint the correct provider on signing
        const transports = session?.user.passkeyTransports as import("@simplewebauthn/browser").AuthenticatorTransportFuture[] | undefined;
        const result = await kit.deployWithExistingPasskey(existingCredentialId, p256PublicKeyHex, transports);
        setContractId(result.contractId);
        setCredentialId(result.credentialId);
        setIsConnected(true);

        // Persist wallet address to NextAuth session (cross-browser support)
        void updateSession({
          smart_account_address: result.contractId,
          credentialId: result.credentialId,
          p256PublicKeyHex,
        }).catch(() => { /* session update is best-effort */ });

        // Persist wallet address to backend MongoDB (cross-device support)
        void fetch("/api/auth/passkey/wallet", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contractId: result.contractId,
            keyId: existingCredentialId,
          }),
        }).catch(() => { /* backend persist is best-effort */ });

        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to deploy wallet";
        setError(msg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [session?.user.passkeyTransports, updateSession]
  );

  const connectWallet = useCallback(async () => {
    const kit = kitRef.current;
    if (!kit) return;
    setIsLoading(true);
    setError(null);

    try {
      const sessionCred = session?.user.credentialId ?? undefined;
      const result = await kit.connectWallet(sessionCred);
      if (result?.contractId) {
        setContractId(result.contractId);
        setCredentialId(result.credentialId);
        setIsConnected(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to connect wallet";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [session?.user.credentialId]);

  const fundWallet = useCallback(
    async (xlmTokenContract: string): Promise<FundResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.fundWallet(xlmTokenContract);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Funding failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const transfer = useCallback(
    async (token: string, to: string, amount: bigint): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.transfer(token, to, amount);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Transfer failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const swap = useCallback(
    async (tokenIn: string, tokenOut: string, amount: bigint, bestHop: string, deadline: number): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.swap(tokenIn, tokenOut, amount, bestHop, deadline);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Swap failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const deposit = useCallback(
    async (usdc: string, amount: bigint, lpPlans: LpPlan[], deadline: number): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.deposit(usdc, amount, lpPlans, deadline);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Deposit failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const redeemSoroswap = useCallback(
    async (pairAddress: string, lpAmount: bigint): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.redeemSoroswap(pairAddress, lpAmount);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Soroswap redeem failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const redeemAqua = useCallback(
    async (poolAddress: string, lpAmount: bigint): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.redeemAqua(poolAddress, lpAmount);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Aqua redeem failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const claimAqua = useCallback(
    async (poolAddress: string): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.claimAqua(poolAddress);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Aqua claim failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const batchRedeem = useCallback(
    async (plans: RedeemPlan[], deadline: number): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.batchRedeem(plans, deadline);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Batch redeem failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const claim = useCallback(
    async (adapter: string, pool: string, rewardToken: string): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.claim(adapter, pool, rewardToken);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Claim failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const queryBalance = useCallback(
    async (tokenId: string): Promise<bigint> => {
      const kit = kitRef.current;
      if (!kit) return 0n;
      try {
        return await kit.queryBalance(tokenId);
      } catch {
        return 0n;
      }
    },
    []
  );

  const createSession = useCallback(
    async (scope: string[], maxAmount: bigint, ttlLedgers: number): Promise<TransactionResult | null> => {
      const kit = kitRef.current;
      if (!kit) return null;
      setError(null);

      try {
        return await kit.createSession(scope, maxAmount, ttlLedgers);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Session creation failed";
        setError(msg);
        return null;
      }
    },
    []
  );

  const queryAquaLpBalance = useCallback(
    async (shareTokenId: string): Promise<bigint> => {
      const kit = kitRef.current;
      if (!kit) return 0n;
      try {
        return await kit.queryAquaLpBalance(shareTokenId);
      } catch {
        return 0n;
      }
    },
    []
  );

  const disconnect = useCallback(() => {
    kitRef.current?.disconnect();
    HoopsPasskeyKit.clearAllWalletStorage();
    setContractId(null);
    setCredentialId(null);
    setIsConnected(false);
    setError(null);
  }, []);

  return (
    <PasskeyContext.Provider
      value={{
        kit: kitRef.current,
        contractId,
        credentialId,
        deployerPublicKey: kitRef.current?.deployerPublicKey ?? null,
        isConnected,
        isLoading,
        error,
        createWallet,
        deployWithExistingPasskey,
        connectWallet,
        fundWallet,
        transfer,
        swap,
        deposit,
        redeemSoroswap,
        redeemAqua,
        claimAqua,
        batchRedeem,
        claim,
        createSession,
        hasSession: kitRef.current?.hasSession ?? false,
        queryBalance,
        getSwapQuotes: async (tokenIn: string, tokenOut: string, amount: bigint) => {
          const kit = kitRef.current;
          if (!kit) return [];
          return kit.getSwapQuotes(tokenIn, tokenOut, amount);
        },
        queryAquaLpBalance,
        disconnect,
      }}
    >
      {children}
    </PasskeyContext.Provider>
  );
};

export const usePasskey = () => {
  const context = useContext(PasskeyContext);
  if (context === undefined) {
    throw new Error("usePasskey must be used within a PasskeyProvider");
  }
  return context;
};
